import { lazyTool } from "../lazy";
import type { ToolDefinition } from "../types";

export const securityTools: ToolDefinition[] = [
  {
    id: "hash-generator",
    name: "Hash Generator",
    description:
      "Compute MD5, SHA-1, SHA-2, SHA-3, and RIPEMD-160 digests, with optional HMAC signing.",
    category: "security",
    keywords: ["hash", "sha256", "sha512", "md5", "sha1", "hmac", "digest", "checksum"],
    component: lazyTool(() => import("./hash-generator")),
    featured: true,
  },
  {
    id: "jwt-parser",
    name: "JWT Parser",
    description: "Decode JWT headers, claims, and signatures without verifying them.",
    category: "security",
    keywords: ["jwt", "token", "bearer", "claims", "payload", "decode", "auth"],
    component: lazyTool(() => import("./jwt-parser")),
  },
  {
    id: "jwt-verify",
    name: "JWT Verifier",
    description: "Verify HS256, HS384, HS512, RS256, and ES256 JWT signatures with Web Crypto.",
    category: "security",
    keywords: ["jwt", "verify", "signature", "hs256", "rs256", "es256", "token", "auth"],
    component: lazyTool(() => import("./jwt-verify")),
  },
  {
    id: "hmac",
    name: "HMAC Generator",
    description: "Sign messages with HMAC using a shared key and any supported hash algorithm.",
    category: "security",
    keywords: ["hmac", "sign", "signature", "mac", "secret", "sha256", "authentication"],
    component: lazyTool(() => import("./hmac")),
  },
  {
    id: "bcrypt",
    name: "Bcrypt Hasher",
    description: "Hash and verify passwords with bcrypt using a configurable cost factor.",
    category: "security",
    keywords: ["bcrypt", "password", "hash", "verify", "salt", "cost", "login"],
    component: lazyTool(() => import("./bcrypt")),
  },
  {
    id: "uuid-ulid",
    name: "UUID / ULID Generator",
    description: "Generate UUID v4, UUID v5, and ULID identifiers in bulk with formatting options.",
    category: "security",
    keywords: ["uuid", "guid", "ulid", "v4", "v5", "identifier", "unique", "id"],
    component: lazyTool(() => import("./uuid-ulid")),
  },
  {
    id: "rsa-keypair",
    name: "RSA / EC Key Pair Generator",
    description: "Generate RSA-OAEP, RSA-PSS, and ECDSA P-256 key pairs as PEM.",
    category: "security",
    keywords: ["rsa", "ecdsa", "keypair", "pem", "pkcs8", "spki", "public key", "private key"],
    component: lazyTool(() => import("./rsa-keypair")),
  },
  {
    id: "password-generator",
    name: "Password Generator",
    description: "Generate strong passwords with chosen character classes and bulk output.",
    category: "security",
    keywords: ["password", "passphrase", "generate", "random", "strength", "entropy"],
    component: lazyTool(() => import("./password-generator")),
  },
  {
    id: "aes-encryption",
    name: "AES Encryption",
    description: "Encrypt and decrypt text with AES-GCM or AES-CBC and a PBKDF2-derived key.",
    category: "security",
    keywords: ["aes", "encrypt", "decrypt", "gcm", "cbc", "pbkdf2", "cipher", "passphrase"],
    component: lazyTool(() => import("./aes-encryption")),
  },
  {
    id: "x509-decoder",
    name: "X.509 Decoder",
    description: "Inspect the fields of a PEM or DER X.509 certificate locally.",
    category: "security",
    keywords: ["x509", "certificate", "pem", "der", "asn1", "tls", "ssl", "san"],
    component: lazyTool(() => import("./x509-decoder")),
  },
  {
    id: "random-bytes",
    name: "Random Bytes Generator",
    description: "Generate cryptographically secure random bytes in hex, Base64, or decimal.",
    category: "security",
    keywords: ["random", "bytes", "entropy", "nonce", "salt", "hex", "base64", "crypto"],
    component: lazyTool(() => import("./random-bytes")),
  },
];
